import React, { useState, useEffect, useContext } from "react";
import PageTitle from "../../Components/PageTitle";
import axios from "axios";
import Swal from "sweetalert2";
import { ConfigContext } from "../../Context/ConfigContext";
import { ContentLoader, NoRecords } from "../../Components/Shimmer";
import Select from "react-select";
import { NavLink, useParams } from "react-router-dom";
import { handleDownloadOptions } from "../../Components/ExportData";

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" }
];

const STATUS_LABELS = {
    0: "Pending",
    1: "Approved",
    2: "Rejected",
    3: "Revise & Resubmit",
    4: "In-Clearence"
};

const STATUS_CLASSES = {
    0: "badge bg-warning-subtle text-warning",
    1: "badge bg-success-subtle text-success",
    2: "badge bg-danger-subtle text-danger",
    3: "badge bg-info-subtle text-info",
    4: "badge bg-secondary-subtle text-secondary"
};

const PendingRequest = () => {
    const { apiURL, apiHeaderJson, user_profile_id } = useContext(ConfigContext);
    const [selectOption, setSelectOption] = useState(null);
    const [projectData, setProjectData] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    const onSelectStatus = (option) => {
        setSelectOption(option ? option.value : "");
    };

    // ----------------------- DOWNLOAD HANDLER -----------------------
    const handleDownload = (downloadType) => {
        const endpoint = "api/GetDashboardForContractor";
        const params = { contractor_id: user_profile_id };

        const columns = [
            { header: "Project Name", width: 30 },
            { header: "Item Name", width: 35 },
            { header: "Submittal Description", width: 25 },
            { header: "Brief Description", width: 25 },
            { header: "Status", width: 25 }
        ];

        const mapRow = (d) => ({
            "Project Name": d?.project_title ?? "-",
            "Item Name": d?.item_name ?? "-",
            "Submittal Description": d?.submittal_description ?? "-",
            "Brief Description": d?.request_brief_description ?? "-",
            "Status": STATUS_LABELS[d?.request_status] ?? "-"
        });

        const responseHandler = (res) =>
            res.data?.data?.tabs?.projects_requests || [];

        handleDownloadOptions(
            endpoint,
            params,
            columns,
            mapRow,
            downloadType,
            apiURL,
            apiHeaderJson,
            responseHandler
        );
    };

    // ----------------------- FETCH LIST -----------------------
    const getPendingProjects = async () => {
        setLoadingList(true);

        try {
            const response = await axios.get(
                `${apiURL}api/GetDashboardForContractor`,
                {
                    params: { contractor_id: user_profile_id },
                    headers: apiHeaderJson
                }
            );
            const filteredData = response.data?.data?.tabs?.projects_requests.filter(row => row.request_status === 0)
            setProjectData(filteredData ?? []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        getPendingProjects();
    }, []);

    return (
        <div className="main-content">
            <div className="page-content">

                {loadingList ? (
                    <ContentLoader />
                ) : (
                    <div className="container-fluid">
                        <PageTitle title="All Pending Request" primary="Home" />

                        <div className="card">
                            <div
                                className="card-header d-flex justify-content-between align-items-center"
                                style={{ background: "#257180" }}
                            >
                                <h4 className="card-title mb-0 text-white">
                                    Pending Request List
                                </h4>
                            </div>

                            {/* Filters & Download */}
                            <div className="card-header border-0">
                                <div className="row align-items-center mb-2 g-3">
                                    <div className="col-md-9"></div>

                                    <div className="col-md-3 col-12">
                                        <div className="dropdown d-flex justify-content-md-end">
                                            <button
                                                type="button"
                                                className="btn btn-light btn-label waves-effect waves-light"
                                                data-bs-toggle="dropdown"
                                            >
                                                <i className="ri-download-2-line label-icon" />
                                                Download Report
                                            </button>

                                            <ul className="dropdown-menu dropdown-menu-end">
                                                <li>
                                                    <a
                                                        className="dropdown-item"
                                                        onClick={() => handleDownload("print")}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="ri-printer-line me-2 text-muted" />
                                                        Print
                                                    </a>
                                                </li>

                                                <li>
                                                    <a
                                                        className="dropdown-item"
                                                        onClick={() => handleDownload("pdf")}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="ri-file-pdf-line me-2 text-muted" />
                                                        PDF
                                                    </a>
                                                </li>

                                                <li>
                                                    <a
                                                        className="dropdown-item"
                                                        onClick={() => handleDownload("excel")}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="ri-file-excel-2-line me-2 text-muted" />
                                                        Excel
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* TABLE */}
                            <div className="card-body">
                               <div className="table-responsive table-card">
                                 <table className="table table-bordered table-striped mb-0">
                                     <thead className="table-light">
                                         <tr>
                                             <th className="text-center">Project Name</th>
                                             <th className="text-center">Item Name</th>
                                             <th className="text-center">Submittal Description</th>
                                             <th className="text-center">Brief Description</th>
                                             <th className="text-center">Status</th>
                                         </tr>
                                     </thead>
                                
                                     <tbody>
                                         {projectData.length > 0 ? (
                                             projectData.map((row) => (
                                                 <tr key={row.category_id}>
                                                     <td className="text-center">
                                                         <NavLink to={`/ProjectProfile/${row.project_id}`}>
                                                             {row?.project_title ?? "-"}
                                                         </NavLink>
                                                     </td>
                                                     <td className="text-center">{row?.item_name ?? "-"}</td>
                                                     <td className="text-center">
                                                         {row?.submittal_description ?? "-"}
                                                     </td>
                                                     <td className="text-center">
                                                         {row?.request_brief_description ?? "-"}
                                                     </td>
                                                     <td className="text-center">
                                                         <span className={STATUS_CLASSES[row.request_status]}>
                                                             {STATUS_LABELS[row.request_status]}
                                                         </span>
                                                     </td>
                                                 </tr>
                                             ))
                                         ) : (
                                             <tr>
                                                 <td colSpan="7">
                                                     <NoRecords />
                                                 </td>
                                             </tr>
                                         )}
                                     </tbody>
                                 </table>
                               </div>
                            </div>

                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default PendingRequest;
